import CourseLessonTree from '@/components/lesson/CourseLessonTree';
import CoursesTreeList from '@/components/shared/CoursesTreeList';
import TreeAddButton from '@/components/shared/TreeAddButton';
import TreeItem from '@/components/shared/TreeItem';
import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import useListService from '@/data/hook/useListService';
import useToggle from '@/data/hook/useToggle';
import { lessonBlocksQueryKeys } from '@/data/services/querykeys';
import Unit from '@/models/Unit';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';
import { CoursesModelsType } from '@/utils/trees/TreeCourses';
import { useTranslation } from 'react-i18next';

type CourseUnitTreeProps = {
  item: Unit;
  parentId: string;
  slug: string;
};

export default function CourseUnitTree({
  item,
  parentId,
  slug,
}: CourseUnitTreeProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.tree',
  });
  const { navigate } = useCourseTreeNavigate();
  const type: CoursesModelsType = 'unit';
  const { tree } = useTreeCourses();
  const { isOpen, toggle, open } = useToggle();
  const key = mountKey({ id: item.id, type });
  const treeItem = { id: item.id, name: item.name };

  const courseNode = tree?.current.getParent(key);
  const moduleNode = tree?.current.getParent(courseNode?.id ?? '');
  const lineNode = tree?.current.getParent(moduleNode?.id ?? '');

  const { results: lessons, isInitialLoading } = useListService({
    ...lessonBlocksQueryKeys.listLessons(slug, item.id, {
      pageSize: ORDENABLE_PAGE_SIZE,
    }),
    enabled: isOpen,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  return (
    <TreeItem
      objKey={key}
      type={type}
      parentId={parentId}
      item={treeItem}
      isLoading={isInitialLoading}
      onToggle={toggle}
      isOpen={isOpen}
      open={open}
    >
      <CoursesTreeList isLoading={isInitialLoading}>
        {lessons.map(lesson => (
          <CourseLessonTree key={lesson.id} item={lesson} parentId={key} />
        ))}
        <TreeAddButton
          label={t('newLesson')}
          onClick={() => {
            navigate(
              ROUTES.ADMIN.COURSE_LINES.LESSON(
                lineNode?.realId ?? 0,
                moduleNode?.realId ?? 0,
                slug,
                item.id,
                'new',
              ),
            );
          }}
        />
      </CoursesTreeList>
    </TreeItem>
  );
}
